import {
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
} from '@fluentui/react-components';
import { ArrowLeft16Regular } from '@fluentui/react-icons';
import { useEffect, useMemo, useState } from 'react';

import { useDataHubClient } from '../../../../../../context/DataHubClientContext.js';
import { useDataEntryDetail } from '../../../../../../hooks/useDataEntryDetail/useDataEntryDetail.js';
import type { DataFile } from '../../../../../../services/dataHubService.interface.js';
import { DataHubService } from '../../../../../../services/dataHubService.js';
import { canLoadDataFilePreview } from '../../utils/detectDataFileParser.js';
import type { ConversationDocument } from '../../utils/parseConversation.js';
import { parseConversation } from '../../utils/parseConversation.js';
import { selectTranscriptFile } from '../../utils/selectTranscriptFile.js';
import { ConversationExpectedActions } from '../ConversationExpectedActions/ConversationExpectedActions.js';
import { ConversationViewer } from '../ConversationViewer/ConversationViewer.js';

import type { DatasetFileViewerProps } from './DatasetFileViewer.interface.js';
import { useStyles } from './DatasetFileViewer.style.js';

const CONVERSATION_NOT_FOUND_MESSAGE =
  'This data entry does not contain a supported conversation file.';
const CONVERSATION_TOO_LARGE_MESSAGE =
  'The selected file is too large or cannot be parsed.';
const CONVERSATION_LOAD_ERROR_MESSAGE =
  'The conversation file could not be loaded.';

function resolveConversationFile(
  files: readonly DataFile[],
  fileId: string | undefined,
  requireExact: boolean,
): DataFile | null {
  if (fileId !== undefined) {
    const requested = files.find((file) => file.id === fileId);
    if (requested !== undefined) {
      return requested;
    }
    if (requireExact) return null;
  }
  if (requireExact) return null;

  return selectTranscriptFile(files);
}

export function DatasetFileViewer({
  annotationTarget,
  datasetId,
  datasetName,
  dataId,
  datasetVersion,
  fileId,
  mode = 'view',
  onAnnotationsSaved,
  onBack,
  onConversationReadyChange,
}: DatasetFileViewerProps) {
  const styles = useStyles();
  const client = useDataHubClient();
  const dataHubService = useMemo(() => new DataHubService(client), [client]);

  const {
    entry,
    isLoading: isEntryLoading,
    error: entryError,
  } = useDataEntryDetail({
    dataSetId: datasetId,
    dataId,
    dataSetVersion: datasetVersion,
    enabled: true,
  });

  const conversationFile = useMemo(
    () =>
      resolveConversationFile(
        entry?.dataFiles ?? [],
        fileId,
        mode === 'annotate',
      ),
    [entry?.dataFiles, fileId, mode],
  );

  const [conversation, setConversation] = useState<ConversationDocument | null>(
    null,
  );
  const [conversationError, setConversationError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    onConversationReadyChange?.(false);
    if (conversationFile === null) {
      setConversation(null);
      setConversationError(null);
      return;
    }

    if (!canLoadDataFilePreview(conversationFile)) {
      setConversation(null);
      setConversationError(CONVERSATION_TOO_LARGE_MESSAGE);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    setConversation(null);
    setConversationError(null);

    void (async () => {
      try {
        const downloadUrl = await dataHubService.getDataEntryFileDownloadUrl(
          datasetId,
          dataId,
          conversationFile.id,
          datasetVersion,
        );
        const source = await dataHubService.readTextFileFromBlob(
          downloadUrl,
          controller.signal,
        );
        const parsedConversation = parseConversation(
          source,
          conversationFile.fileName,
        );
        if (!cancelled) {
          setConversation(parsedConversation);
          onConversationReadyChange?.(true);
        }
      } catch (caughtError) {
        if (!cancelled) {
          setConversationError(
            caughtError instanceof Error
              ? caughtError.message
              : CONVERSATION_LOAD_ERROR_MESSAGE,
          );
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    dataHubService,
    dataId,
    datasetId,
    datasetVersion,
    conversationFile,
    onConversationReadyChange,
  ]);

  // Prefer the concrete file name: a data entry can hold multiple, distinct
  // annotatable files, so the entry/meeting title would be ambiguous here.
  const title =
    conversationFile?.fileName ?? entry?.meetingTitle ?? entry?.name ?? dataId;

  return (
    <div className={styles.root}>
      {onBack !== undefined &&
        (isEntryLoading ||
          entryError !== null ||
          conversationFile === null ||
          conversationError !== null ||
          conversation === null) && (
          <Button
            appearance="subtle"
            size="small"
            className={styles.compactAction}
            icon={<ArrowLeft16Regular />}
            aria-label="Back to annotation setup"
            onClick={onBack}
          >
            Back
          </Button>
        )}
      <div className={styles.content}>
        {isEntryLoading ? (
          <Spinner label="Loading data entry" />
        ) : entryError !== null ? (
          <MessageBar intent="error" role="alert">
            <MessageBarBody>{entryError}</MessageBarBody>
          </MessageBar>
        ) : entry !== null && conversationFile === null ? (
          <MessageBar intent="warning">
            <MessageBarBody>{CONVERSATION_NOT_FOUND_MESSAGE}</MessageBarBody>
          </MessageBar>
        ) : conversationError !== null ? (
          <MessageBar intent="error" role="alert">
            <MessageBarBody>{conversationError}</MessageBarBody>
          </MessageBar>
        ) : conversation === null ? (
          <Spinner label="Loading conversation" />
        ) : mode === 'annotate' && annotationTarget !== undefined ? (
          <ConversationExpectedActions
            target={annotationTarget}
            conversation={conversation}
            datasetId={datasetId}
            datasetName={datasetName}
            dataId={dataId}
            entryName={entry?.name}
            fileName={conversationFile?.fileName}
            fileId={conversationFile?.id}
            heading={title}
            isScrollable
            fillHeight
            onSaved={onAnnotationsSaved}
            onBack={onBack}
          />
        ) : (
          <ConversationViewer
            conversation={conversation}
            heading={title}
            isScrollable
            fillHeight
          />
        )}
      </div>
    </div>
  );
}
